import React = require('react');

import { ButtonType, ButtonWidget, CheckBoxWithLabelWidget } from '@tableau/widgets';

import { Messages } from '../../../codegen/Localize';
import { ButtonWithSpinner } from '../../../common/react/components/ButtonWithSpinner';
import { SiteRolePickerMenu } from '../../../common/react/components/SiteRolePickerMenu';
import { Location } from '../../../common/react/Location';
import { L10n } from '../../../ts/L10n/L10n';
import { IGetGroupDetailInfoResult } from '../../../ts/models/IGroupInfo';
import { GrantLicenseModes } from '../../../ts/ServerApi/requests/SyncActiveDirectoryGroupRequest';
import { SiteRoles } from '../../../ts/ServerApi/types/ISiteRole';
import { ToasterService } from '../../../ts/services/ToasterService';
import { DeleteGroupAction } from '../../../userManagement/groups/services/actions/DeleteGroupAction';
import { UpdateGroupsMinimumSiteRoleAction } from '../../../userManagement/groups/services/actions/UpdateGroupsMinimumSiteRoleAction';
import { SiteRoleOption, SiteRoleOptionsResource} from '../../../userManagement/services/resources/SiteRoleOptions';
import { getAppStore } from '../../AppStore';
import { siteUrlsSelector } from '../../selectors/SiteUrlsSelectors';

export interface GroupDetailsSubspaceProps {
  readonly group: IGetGroupDetailInfoResult;
}

interface GroupDetailsSubspaceState {
  isSavingMinimumSiteRole: boolean;
  isChangingMinimumSiteRoleDetails: boolean;
  groupDetailsModel: IGroupDetailsModel;
  licenseUponLoginCheckboxDisabled: boolean;
}

export interface IGroupDetailsModel {
  domainName: string;
  createdAt: string;
  updatedAt: string;
  isAllUsersGroup: boolean;
  minimumSiteRole?: string;
  grantLicenseMode?: GrantLicenseModes;
}

export class GroupDetailsSubspaceComponent extends React.Component<GroupDetailsSubspaceProps, GroupDetailsSubspaceState> {

  private isLocalGroup: boolean = !(this.props.group.domainName && this.props.group.domainName !== 'local');
  private originalGroupDetails: IGetGroupDetailInfoResult = this.props.group;

  public constructor(props: GroupDetailsSubspaceProps) {
    super(props);
    this.state = {
      isSavingMinimumSiteRole: false,
      isChangingMinimumSiteRoleDetails: false,
      groupDetailsModel: {
        domainName: this.props.group.domainName,
        createdAt: this.props.group.createdAt,
        updatedAt: this.props.group.updatedAt,
        isAllUsersGroup: this.props.group.isAllUsersGroup,
        minimumSiteRole: (!this.props.group.minimumSiteRole) ?
                            SiteRoles.Unlicensed : this.props.group.minimumSiteRole,
        grantLicenseMode: this.props.group.grantLicenseMode
      },
      licenseUponLoginCheckboxDisabled: (!this.isLocalGroup && this.props.group.minimumSiteRole === SiteRoles.Unlicensed)
    };
  }

  public render() {
    return (
      <div className="tb-details">
        <table>
          <tbody>
            {this.isLocalGroup &&
              <tr>
                <td className="tb-label">{Messages.column_domainName()}</td>
                <td className="tb-details-value" tb-test-id="tb-domain-value-id">{this.originalGroupDetails.domainName}</td>
                <td className="tb-details-button-cell"></td>
              </tr>}
            <tr>
              <td className="tb-label">{Messages.column_created()}</td>
              <td className="tb-details-value" tb-test-id="tb-created-id">{L10n.localizedDateTime(this.originalGroupDetails.createdAt)}</td>
              <td className="tb-details-button-cell"></td>
            </tr>
            <tr>
              <td className="tb-label">{Messages.column_updated()}</td>
              <td className="tb-details-value" tb-test-id="tb-modified-id">{L10n.localizedDateTime(this.originalGroupDetails.updatedAt)}</td>
              <td className="tb-details-button-cell"></td>
            </tr>
            {this.getEditSiteRoleAndLicenseUponLoginSettings()}
            {!this.originalGroupDetails.isAllUsersGroup &&
            <tr className="tb-more-actions">
              <td></td>
              <td></td>
              <td>
                <ButtonWidget
                  buttonType={ButtonType.Destructive}
                  testId="settings-action-delete"
                  onClick={this.handleDeleteGroup.bind(this)}
                >{Messages.deleteGroup_action()}</ButtonWidget>
              </td>
            </tr>}
          </tbody>
        </table>
      </div>
    );
  }

  private readonly getSiteRoleOptionBySiteRoleCallback = (siteRole: string): SiteRoleOption => {
    return SiteRoleOptionsResource.instance.getSiteRoleOptionBySiteRole(siteRole);
  }

  private getEditSiteRoleAndLicenseUponLoginSettings = (): JSX.Element => {
    return(
      <tr>
        <td className="tb-label">{Messages.groupsMinimumSiteRole()}</td>
        {!this.isLocalGroup &&
          <td className="tb-details-value">
            {this.getMinimumSiteRolePicker()}
            {this.getLicenseUponLoginCheckbox()}
          </td>
        }
        {this.isLocalGroup &&
          <td className="tb-details-value">
            {this.getLicenseUponLoginCheckbox()}
            {this.getMinimumSiteRolePicker()}
          </td>
        }
        <td className="tb-details-button-cell">
          <ButtonWithSpinner
            buttonType={ButtonType.Go}
            testId="group-details-default-site-role-save"
            disabled={!this.state.isChangingMinimumSiteRoleDetails}
            clicked={this.handleSaveGroupSettings}
            title={Messages.save()}
            showSpinner={this.state.isSavingMinimumSiteRole}
            onDarkBackground={true}
            />
          <br/>
          <ButtonWidget
            buttonType={ButtonType.Outline}
            testId="group-details-default-site-role-cancel"
            onClick={this.handleCancelGroupSettings}
            disabled={!this.state.isChangingMinimumSiteRoleDetails}
            style={{marginTop: '6px'}}
          >{Messages.cancel()}</ButtonWidget>
        </td>
      </tr>
    );
  }

  private getMinimumSiteRolePicker = (): JSX.Element => {
    let siteRoleOptions = SiteRoleOptionsResource.instance.getAllSiteRoleOptionsExcept(SiteRoles.ServerAdministrator);
    let minimumSiteRole = this.isLocalGroup && this.state.groupDetailsModel.minimumSiteRole === SiteRoles.Unlicensed ?
                            SiteRoles.ExplorerCanPublish : this.state.groupDetailsModel.minimumSiteRole;
    let disableRoleOptions = this.isLocalGroup ? [SiteRoleOptionsResource.instance.getSiteRoleOptionBySiteRole(SiteRoles.Unlicensed)] : [];
    return (<span className="tb-box" style={{width: '220px'}}>
              <SiteRolePickerMenu
                siteRoleOptions={siteRoleOptions}
                getSiteRoleOptionBySiteRole={this.getSiteRoleOptionBySiteRoleCallback}
                onOptionSelected={this.onMinimumSiteRoleSelected}
                selectedRole={SiteRoleOptionsResource.instance.getSiteRoleOptionBySiteRole(minimumSiteRole)}
                isDisabled={(this.isLocalGroup && this.state.groupDetailsModel.grantLicenseMode !== GrantLicenseModes.ON_LOGIN) || this.state.isSavingMinimumSiteRole}
                disabledRoleOptions={disableRoleOptions}
                style={{width: '220px'}}
              />
            </span>);
  }

  private getLicenseUponLoginCheckbox = (): JSX.Element => {
    return(<div>
              <CheckBoxWithLabelWidget
                checked={this.state.groupDetailsModel.grantLicenseMode === GrantLicenseModes.ON_LOGIN}
                handleChange={this.toggleLicenseUponLogin}
                containerStyle={{marginLeft: '0px', marginTop: this.isLocalGroup ? '0px' : '16px',
                  marginBottom: this.isLocalGroup ? '16px' : '0px'}}
                label= {Messages.grantLicenseUponLoginCheckboxLabel()}
                testId="license-upon-login"
                disabled={this.state.licenseUponLoginCheckboxDisabled}
              />
          </div>);
  }

  private hasChangedMinimumSiteRoleDetails = (grantLicenseMode: GrantLicenseModes, minimumSiteRole: string) => {
    let originalMinimumSiteRole = !this.originalGroupDetails.minimumSiteRole ? SiteRoles.Unlicensed : this.originalGroupDetails.minimumSiteRole;
    return ((grantLicenseMode !== this.originalGroupDetails.grantLicenseMode) ||
        (minimumSiteRole !== originalMinimumSiteRole));
  }

  private toggleLicenseUponLogin = (checked: boolean) => {
    let defaultLicenseMode = this.isLocalGroup ? undefined : GrantLicenseModes.ON_SYNC;
    let defaultMinimumSiteRole = this.isLocalGroup ? SiteRoles.ExplorerCanPublish : this.state.groupDetailsModel.minimumSiteRole;
    let minimumSiteRole = this.isLocalGroup && !checked
       ? SiteRoles.Unlicensed : defaultMinimumSiteRole;
    let groupDetailsModel: IGroupDetailsModel = {...this.state.groupDetailsModel,
                                                 grantLicenseMode: checked ? GrantLicenseModes.ON_LOGIN : defaultLicenseMode,
                                                 minimumSiteRole};
    let isChangingMinimumSiteRoleDetails = this.hasChangedMinimumSiteRoleDetails(groupDetailsModel.grantLicenseMode, groupDetailsModel.minimumSiteRole);
    this.setState({groupDetailsModel, isChangingMinimumSiteRoleDetails});
  }

  private onMinimumSiteRoleSelected = (siteRoleOption: SiteRoleOption) => {
    let grantLicenseMode = siteRoleOption.role === SiteRoles.Unlicensed ? GrantLicenseModes.ON_SYNC : this.state.groupDetailsModel.grantLicenseMode;
    let licenseUponLoginCheckboxDisabled = siteRoleOption.role === SiteRoles.Unlicensed;
    let groupDetailsModel: IGroupDetailsModel = {...this.state.groupDetailsModel, minimumSiteRole: siteRoleOption.role, grantLicenseMode};
    let isChangingMinimumSiteRoleDetails = this.hasChangedMinimumSiteRoleDetails(groupDetailsModel.grantLicenseMode, groupDetailsModel.minimumSiteRole);
    this.setState({groupDetailsModel, isChangingMinimumSiteRoleDetails, licenseUponLoginCheckboxDisabled});
  }

  private handleDeleteGroup = () => {
    DeleteGroupAction.deleteGroup(this.originalGroupDetails).then(() => {
      ToasterService.instance.markToastsAsDoNotDismissOnLocationChange();
      const siteUrls = siteUrlsSelector(getAppStore().getState());
      Location.instance.setUrl(siteUrls.groupsUrl());
    });
  }

  private handleSaveGroupSettings = () => {
    this.setState({isSavingMinimumSiteRole: true});
    const savingMinimumSiteRole = this.state.groupDetailsModel.minimumSiteRole;
    const grantLicenseMode = this.state.groupDetailsModel.grantLicenseMode;
    UpdateGroupsMinimumSiteRoleAction.executeInPlace(this.originalGroupDetails, savingMinimumSiteRole, grantLicenseMode)
      .then(() => {
        this.originalGroupDetails.minimumSiteRole = savingMinimumSiteRole;
        this.originalGroupDetails.grantLicenseMode = grantLicenseMode;
      })
      .finally(() => {
        let licenseUponLoginCheckboxDisabled = (savingMinimumSiteRole === SiteRoles.Unlicensed && !this.isLocalGroup) ? true : false;
        this.setState({isSavingMinimumSiteRole: false, isChangingMinimumSiteRoleDetails: false, licenseUponLoginCheckboxDisabled});
      });
  }

  private handleCancelGroupSettings = () => {
    let minimumSiteRole = this.isLocalGroup && this.props.group.grantLicenseMode !== GrantLicenseModes.ON_LOGIN
                           ? SiteRoles.Unlicensed : this.props.group.minimumSiteRole;
    let groupDetailsModel: IGroupDetailsModel = {...this.state.groupDetailsModel,
      grantLicenseMode: this.originalGroupDetails.grantLicenseMode, minimumSiteRole};
    let isChangingMinimumSiteRoleDetails  = false;
    let licenseUponLoginCheckboxDisabled = (minimumSiteRole === SiteRoles.Unlicensed && !this.isLocalGroup) ? true : false;
    this.setState({groupDetailsModel, isChangingMinimumSiteRoleDetails, licenseUponLoginCheckboxDisabled});
  }
}

export const GroupDetailsSubspace: React.ComponentType<GroupDetailsSubspaceProps> = GroupDetailsSubspaceComponent;
